import { api } from '../apiClient';

export interface ProfileData {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  is_owner: boolean;
  profile: {
    bio: string;
    avatar_url: string | null;
    phone_number: string;
    date_of_birth: string | null;
    address: string;
  } | null;
  email_verified?: boolean;
  stats?: {
    total_passes: number;
    total_bookings: number;
    total_orders: number;
    total_addons: number;
  };
}

export interface Pass {
  id: string;
  identifier: string;
  category_name: string | null;
  cluster: {
    id: number;
    name: string;
    color: string | null;
    icon: string | null;
  } | null;
  event_title: string | null;
  event_slug: string | null;
  expires: string | null;
  image_url: string | null;
  qr_url: string;
}

export interface Booking {
  id: string;
  date: string | null;
  activity_name: string | null;
  activity_slug: string | null;
  option_name: string | null;
  image_url: string | null;
}

export interface Order {
  id: number;
  total: number;
  currency: {
    symbol: string;
    code: string;
  };
  closed_at: string | null;
  items_count: number;
}

export interface AddonOrder {
  id: number;
  addon_name: string;
  quantity: number;
  total_price: number;
  sale_date: string | null;
  delivered: boolean;
  image_url: string | null;
}

export interface Address {
  id: number;
  name: string;
  phone: string;
  apartment_address: string;
  area: string;
  zone: string;
  city: string;
  string_address: string;
  is_default: boolean;
}

export interface UpdateProfileData {
  first_name?: string;
  last_name?: string;
  bio?: string;
  phone_number?: string;
  date_of_birth?: string;
  address?: string;
  profile_picture?: File;
  cropped_avatar?: string;
}

export const profileService = {
  getProfile: async (username: string): Promise<ProfileData> => {
    const response = await api.get(`/profile/${username}/`);
    return response.data;
  },

  updateProfile: async (data: UpdateProfileData): Promise<{ user: any; message: string }> => {
    const formData = new FormData();
    
    if (data.first_name) formData.append('first_name', data.first_name);
    if (data.last_name) formData.append('last_name', data.last_name);
    if (data.bio !== undefined) formData.append('bio', data.bio);
    if (data.phone_number) formData.append('phone_number', data.phone_number);
    if (data.date_of_birth) formData.append('date_of_birth', data.date_of_birth);
    if (data.address) formData.append('address', data.address);
    if (data.profile_picture) formData.append('profile_picture', data.profile_picture);
    if (data.cropped_avatar) formData.append('cropped_avatar', data.cropped_avatar);

    const response = await api.put('/profile/update/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getPasses: async (): Promise<{ passes: Pass[] }> => {
    const response = await api.get('/profile/passes/');
    return response.data;
  },

  getBookings: async (): Promise<{ upcoming: Booking[]; past: Booking[] }> => {
    const response = await api.get('/profile/bookings/');
    return response.data;
  },

  getOrders: async (): Promise<{ orders: Order[] }> => {
    const response = await api.get('/profile/orders/');
    return response.data;
  },

  getAddons: async (): Promise<{ addons: AddonOrder[] }> => {
    const response = await api.get('/profile/addons/');
    return response.data;
  },

  getAddresses: async (): Promise<{ addresses: Address[] }> => {
    const response = await api.get('/profile/addresses/');
    return response.data;
  },

  getPassQRCode: async (passId: string): Promise<{ qr_code: string; qr_url: string; identifier: string; event_title: string | null }> => {
    const response = await api.get(`/profile/passes/${passId}/qr/`);
    return response.data;
  },
};

